import { useState } from "react";
import api from "../../services/api";

// Même garde-fou que Budget.jsx (onglets Scénarios/Comparaison) : le claim
// JWT `plan` n'existe plus dans aucun token émis depuis le passage au modèle
// socle + modules, donc planRank retombe systématiquement à 0 pour tout le
// monde. Tant que SAAS_MODE est désactivé (pas de REACT_APP_SAAS_MODE=true),
// ce verrou ne doit pas masquer le bouton.
const SAAS_MODE = process.env.REACT_APP_SAAS_MODE === 'true';

const STATUS_COLORS = { brouillon: "#f59e0b", validé: "#16a34a", archivé: "#6b7280" };
const TYPE_LABELS   = { budget: "Budget", prévisionnel: "Prévisionnel", reforecast: "Reforecast", business_plan: "Business Plan" };
const TYPE_COLORS   = { budget: "#3b82f6", prévisionnel: "#8b5cf6", reforecast: "#f59e0b", business_plan: "#1a9e72" };
const HYPO_COLORS   = { central: "#3b82f6", optimiste: "#16a34a", pessimiste: "#f59e0b", crise: "#ef4444", libre: "#8b5cf6" };
const HYPO_LABELS   = { central: "Central", optimiste: "Optimiste", pessimiste: "Pessimiste", crise: "Crise", libre: "Libre" };

const TEMPLATES = [
  { id: "central",    icon: "🎯", label: "Scénario Central",    hypothesis: "central",    description: "Base de référence — projection réaliste", growth: 0 },
  { id: "optimiste",  icon: "🚀", label: "Scénario Optimiste",  hypothesis: "optimiste",  description: "CA +20% · Charges variables proportionnelles", growth: 20 },
  { id: "pessimiste", icon: "⚠️",  label: "Scénario Pessimiste", hypothesis: "pessimiste", description: "CA -20% · Charges réduites proportionnellement", growth: -20 },
  { id: "crise",      icon: "🆘", label: "Scénario de Crise",   hypothesis: "crise",      description: "CA au point mort — seuil de rentabilité", growth: -40 },
  { id: "libre",      icon: "✏️",  label: "Scénario Libre",      hypothesis: "libre",      description: "Hypothèses personnalisées", growth: 0 },
];

function ScenarioCard({ sc, onSelect, isActive, onDuplicate, onArchive, onDelete, onValidate }) {
  const hypo = sc.hypothesis || "central";
  return (
    <div style={{
      background: "var(--card-bg,#fff)", border: `2px solid ${isActive ? "#1a9e72" : "var(--border-color,#e2e8f0)"}`,
      borderRadius: 12, padding: "16px 20px", cursor: "pointer",
      transition: "border-color .15s",
    }} onClick={onSelect}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{sc.name}</div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, background: `${TYPE_COLORS[sc.type]}20`, color: TYPE_COLORS[sc.type], fontWeight: 600 }}>
              {TYPE_LABELS[sc.type] || sc.type}
            </span>
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, background: `${STATUS_COLORS[sc.status]}20`, color: STATUS_COLORS[sc.status], fontWeight: 600 }}>
              {sc.status}
            </span>
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, background: `${HYPO_COLORS[hypo]}18`, color: HYPO_COLORS[hypo], fontWeight: 600 }}>
              {HYPO_LABELS[hypo] || hypo}
            </span>
            {sc.growth_rate !== 0 && sc.growth_rate != null && (
              <span style={{ fontSize: 10, color: sc.growth_rate > 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                {sc.growth_rate > 0 ? "+" : ""}{sc.growth_rate}%
              </span>
            )}
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Exercice {sc.fiscal_year}</span>
          </div>
          {sc.description && <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>{sc.description}</div>}
          <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 6 }}>
            Créé le {new Date(sc.created_at).toLocaleDateString("fr-FR")}
            {sc.validated_at && ` · Validé le ${new Date(sc.validated_at).toLocaleDateString("fr-FR")}`}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }} onClick={e => e.stopPropagation()}>
          {sc.status === 'brouillon' && (
            <button onClick={onValidate} style={{ padding: "4px 10px", background: "#16a34a22", color: "#16a34a", border: "1px solid #16a34a44", borderRadius: 6, fontSize: 11, cursor: "pointer" }}>
              ✓ Valider
            </button>
          )}
          <button onClick={onDuplicate} style={{ padding: "4px 10px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 11, cursor: "pointer" }}>
            Dupliquer
          </button>
          {sc.status !== 'archivé' && (
            <button onClick={onArchive} style={{ padding: "4px 10px", background: "#6b728022", color: "#6b7280", border: "1px solid #6b728044", borderRadius: 6, fontSize: 11, cursor: "pointer" }}>
              Archiver
            </button>
          )}
          <button onClick={onDelete} style={{ padding: "4px 10px", background: "#ef444422", color: "#ef4444", border: "1px solid #ef444444", borderRadius: 6, fontSize: 11, cursor: "pointer" }}>
            Supprimer
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ScenariosTab({ scenarios, activeScenarioId, onSelect, onRefresh, currentCompanyId, planRank }) {
  const [showCreate, setShowCreate]   = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [compareA, setCompareA]       = useState("");
  const [compareB, setCompareB]       = useState("");
  const [comparing, setComparing]     = useState(false);
  const [form, setForm] = useState({ name: "", fiscal_year: new Date().getFullYear(), type: "budget", description: "", base_scenario_id: "", growth_percent: "0", hypothesis: "central" });
  const [tplStep, setTplStep] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState("");

  const create = async () => {
    if (!form.name || !form.fiscal_year) return;
    setSaving(true);
    try {
      await api.budget.createScenario({ ...form, company_id: currentCompanyId, base_scenario_id: form.base_scenario_id || null, growth_percent: parseFloat(form.growth_percent) || 0 });
      onRefresh && onRefresh();
      setShowCreate(false);
      setTplStep(true);
      setMsg("✓ Scénario créé");
    } catch (e) { setMsg("Erreur : " + e.message); }
    finally { setSaving(false); setTimeout(() => setMsg(""), 3000); }
  };

  const applyTemplate = (tpl) => {
    setForm(f => ({ ...f, hypothesis: tpl.hypothesis, growth_percent: String(tpl.growth), name: tpl.label + " " + f.fiscal_year, description: tpl.description }));
    setTplStep(false);
  };

  const duplicate = async (sc) => {
    const name = prompt("Nom du nouveau scénario :", `${sc.name} (copie)`);
    if (!name) return;
    const pct  = prompt("Évolution globale (%) :", "0");
    if (pct === null) return;
    try {
      await api.budget.duplicateScenario(sc.id, { name, growth_percent: parseFloat(pct) || 0 });
      onRefresh && onRefresh();
    } catch (e) { setMsg("Erreur : " + e.message); }
  };

  const archive = async (sc) => {
    if (!window.confirm(`Archiver "${sc.name}" ?`)) return;
    try { await api.budget.archiveScenario(sc.id); onRefresh && onRefresh(); }
    catch (e) { setMsg("Erreur : " + e.message); }
  };

  const deleteSc = async (sc) => {
    if (!window.confirm(`Supprimer "${sc.name}" ? Cette action est irréversible.`)) return;
    try { await api.budget.deleteScenario(sc.id); onRefresh && onRefresh(); if (activeScenarioId === sc.id) onSelect(null); }
    catch (e) { setMsg("Erreur : " + e.message); }
  };

  const validate = async (sc) => {
    if (!window.confirm(`Valider le budget "${sc.name}" ?`)) return;
    try { await api.budget.validateScenario(sc.id); onRefresh && onRefresh(); }
    catch (e) { setMsg("Erreur : " + e.message); }
  };

  const runCompare = async () => {
    if (!compareA || !compareB) return;
    setComparing(true);
    try { setCompareData(await api.budget.compareScenarios(compareA, compareB)); }
    catch (e) { setMsg("Erreur : " + e.message); }
    finally { setComparing(false); }
  };

  const inputSt = { width: "100%", padding: "8px 11px", border: "1px solid var(--border-color,#e2e8f0)", borderRadius: 8, fontSize: 13, background: "var(--input-bg,#fff)", color: "var(--text,#1f2937)", boxSizing: "border-box" };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, alignItems: "center" }}>
        <div style={{ flex: 1, fontSize: 13, color: "var(--text-muted)" }}>{scenarios.length} scénario(s)</div>
        {msg && <span style={{ fontSize: 12, color: msg.startsWith("✓") ? "#16a34a" : "#dc2626" }}>{msg}</span>}
        {(planRank >= 2 || !SAAS_MODE) && (
          <button onClick={() => setShowCompare(true)} style={{ padding: "7px 13px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 12, cursor: "pointer" }}>
            ⚖ Comparer 2 scénarios
          </button>
        )}
        <button onClick={() => setShowCreate(true)} style={{ padding: "7px 14px", background: "#1a9e72", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          + Nouveau scénario
        </button>
      </div>

      {scenarios.length === 0 ? (
        <div style={{ textAlign: "center", padding: 80, color: "var(--text-muted)" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
          <div style={{ fontSize: 15, fontWeight: 600 }}>Aucun scénario</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>Créez votre premier scénario budgétaire</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 14 }}>
          {scenarios.map(sc => (
            <ScenarioCard key={sc.id} sc={sc} isActive={activeScenarioId === sc.id}
              onSelect={() => onSelect(sc)}
              onDuplicate={() => duplicate(sc)}
              onArchive={() => archive(sc)}
              onDelete={() => deleteSc(sc)}
              onValidate={() => validate(sc)}
            />
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "var(--card-bg,#fff)", borderRadius: 14, padding: 28, width: tplStep ? 520 : 480 }}>
            {tplStep ? (
              <>
                <h3 style={{ margin: "0 0 6px 0", fontSize: 16, fontWeight: 700 }}>Démarrer avec un scénario</h3>
                <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 18 }}>Choisissez un modèle ou partez d'une feuille blanche</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {TEMPLATES.map(tpl => (
                    <div key={tpl.id} onClick={() => applyTemplate(tpl)} style={{
                      display: "flex", alignItems: "center", gap: 14, padding: "12px 16px",
                      border: `2px solid ${HYPO_COLORS[tpl.hypothesis]}30`, borderRadius: 10, cursor: "pointer",
                      background: `${HYPO_COLORS[tpl.hypothesis]}08`, transition: "border-color .12s",
                    }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = HYPO_COLORS[tpl.hypothesis]}
                      onMouseLeave={e => e.currentTarget.style.borderColor = `${HYPO_COLORS[tpl.hypothesis]}30`}
                    >
                      <span style={{ fontSize: 24 }}>{tpl.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: HYPO_COLORS[tpl.hypothesis] }}>{tpl.label}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{tpl.description}</div>
                      </div>
                      {tpl.growth !== 0 && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: tpl.growth > 0 ? "#16a34a" : "#dc2626" }}>
                          {tpl.growth > 0 ? "+" : ""}{tpl.growth}%
                        </span>
                      )}
                      <span style={{ fontSize: 16, color: "#94a3b8" }}>›</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                  <button onClick={() => setShowCreate(false)} style={{ padding: "8px 16px", border: "1px solid #e5e7eb", borderRadius: 8, background: "transparent", cursor: "pointer", fontSize: 13 }}>Annuler</button>
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                  <button onClick={() => setTplStep(true)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: "#64748b" }}>←</button>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Configurer le scénario</h3>
                  <span style={{ fontSize: 11, padding: "2px 9px", borderRadius: 8, background: `${HYPO_COLORS[form.hypothesis]}18`, color: HYPO_COLORS[form.hypothesis], fontWeight: 600 }}>
                    {HYPO_LABELS[form.hypothesis]}
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div>
                    <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>Nom du scénario *</label>
                    <input style={inputSt} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Budget 2025 — Version initiale" />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>Exercice fiscal</label>
                      <input type="number" style={inputSt} value={form.fiscal_year} onChange={e => setForm(f => ({ ...f, fiscal_year: parseInt(e.target.value) }))} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>Type</label>
                      <select style={inputSt} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                        <option value="budget">Budget</option>
                        <option value="prévisionnel">Prévisionnel</option>
                        <option value="reforecast">Reforecast</option>
                        <option value="business_plan">Business Plan</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>Basé sur un scénario existant</label>
                    <select style={inputSt} value={form.base_scenario_id} onChange={e => setForm(f => ({ ...f, base_scenario_id: e.target.value }))}>
                      <option value="">Depuis zéro (lignes standards PCG)</option>
                      {scenarios.map(s => <option key={s.id} value={s.id}>{s.name} ({s.fiscal_year})</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>Évolution des montants (%)</label>
                    <input type="number" step="0.5" style={inputSt} value={form.growth_percent} onChange={e => setForm(f => ({ ...f, growth_percent: e.target.value }))} />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>Description</label>
                    <textarea style={{ ...inputSt, height: 60, resize: "vertical" }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
                  <button onClick={() => { setShowCreate(false); setTplStep(true); }} style={{ padding: "8px 16px", border: "1px solid #e5e7eb", borderRadius: 8, background: "transparent", cursor: "pointer", fontSize: 13 }}>Annuler</button>
                  <button onClick={create} disabled={saving || !form.name} style={{ padding: "8px 16px", background: "#1a9e72", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                    {saving ? "Création..." : "Créer le scénario"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Compare modal */}
      {showCompare && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "var(--card-bg,#fff)", borderRadius: 14, padding: 28, width: 700, maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
            <h3 style={{ margin: "0 0 18px 0", fontSize: 16, fontWeight: 700 }}>Comparer deux scénarios</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
              {[["A (référence)", compareA, setCompareA], ["B (comparaison)", compareB, setCompareB]].map(([label, val, setVal]) => (
                <div key={label}>
                  <label style={{ display: "block", fontSize: 12, color: "var(--text-muted)", marginBottom: 4 }}>Scénario {label}</label>
                  <select style={inputSt} value={val} onChange={e => setVal(e.target.value)}>
                    <option value="">Choisir...</option>
                    {scenarios.map(s => <option key={s.id} value={s.id}>{s.name} ({s.fiscal_year})</option>)}
                  </select>
                </div>
              ))}
            </div>
            <button onClick={runCompare} disabled={!compareA || !compareB || comparing} style={{ padding: "8px 16px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600, marginBottom: 16, width: 160 }}>
              {comparing ? "Analyse..." : "Comparer"}
            </button>

            {compareData && (
              <div style={{ overflowY: "auto", flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>
                  {compareData.scenA?.name} vs {compareData.scenB?.name}
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f9fafb" }}>
                      {["Compte","Désignation",`${compareData.scenA?.name} (A)`,`${compareData.scenB?.name} (B)`,"Différence","Écart %"].map(h => (
                        <th key={h} style={{ padding: "7px 8px", textAlign: "right", fontWeight: 600, color: "#6b7280", borderBottom: "1px solid #e5e7eb", whiteSpace: "nowrap", fontSize: 11 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {compareData.rows?.map(r => (
                      <tr key={r.account_id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "6px 8px", color: "#6b7280", textAlign: "right" }}>{r.account_id}</td>
                        <td style={{ padding: "6px 8px", textAlign: "right" }}>{r.account_name}</td>
                        <td style={{ padding: "6px 8px", textAlign: "right" }}>{(r.totalA || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €</td>
                        <td style={{ padding: "6px 8px", textAlign: "right" }}>{(r.totalB || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €</td>
                        <td style={{ padding: "6px 8px", textAlign: "right", color: r.diff >= 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                          {r.diff >= 0 ? "+" : ""}{(r.diff || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })} €
                        </td>
                        <td style={{ padding: "6px 8px", textAlign: "right", color: r.diffPct >= 0 ? "#16a34a" : "#dc2626" }}>
                          {r.diffPct >= 0 ? "+" : ""}{(r.diffPct || 0).toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button onClick={() => { setShowCompare(false); setCompareData(null); }} style={{ marginTop: 16, padding: "8px 16px", border: "1px solid #e5e7eb", borderRadius: 8, background: "transparent", cursor: "pointer", fontSize: 13 }}>Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}
